import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    PluginContext,
    ReadOnlyExtensionAPI
} from '../../types'
import {isPinned, isPinnedToContainer, updateTargetLayout} from '../common'
import {
    getMeasurementsPointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getTargetLayoutPointer,
    getVirtualStructurePointer
} from '../../conversionDal/utils/pointer'
import {
    BOTTOM_PADDING,
    DEFAULT_PADDING_BOTTOM,
    HeightType,
    LIST_ITEM_PADDING_TOP,
    TOP_BOTTOM_RELATION,
    TOP_PADDING
} from './constants'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {VIRTUAL_TAG} from '../grouping/constants'
import _ from 'lodash'
import type {HorizontalScaleAndSizePluginAPI} from '../horizontalScaleAndSize/types'

const updatePinnedVerticalMargins = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {top, bottom} = margins

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                top,
                bottom
            },
            type: 'FixedItemLayout'
        })
    })
}

//TODO: change this once we have a way heuristic to pinned containers
const updatePinnedToContainerVerticalMargins = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
    const {itemLayout: targetItemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
    const {margins, alignSelf, justifySelf} = itemLayout
    const {top, bottom} = margins

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, targetItemLayout, {
            alignSelf,
            justifySelf,
            margins: {
                top,
                bottom
            },
            type: 'GridItemLayout'
        })
    })
}

const updateComponentHeight = (
    componentStageData: ComponentStageData,
    height: number,
    heightType: HeightType = HeightType.Fixed
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {componentLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {}

    if (heightType === HeightType.Auto) {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    type: 'auto'
                },
                type: 'ComponentLayout'
            }
        })
    } else if (heightType === HeightType.AspectRatio) {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    type: 'aspectRatio',
                    value: height
                },
                type: 'ComponentLayout'
            }
        })
    } else {
        updateTargetLayout(componentStageData, {
            componentLayout: {
                ...componentLayout,
                height: {
                    value: height,
                    type: 'spx'
                },
                type: 'ComponentLayout'
            }
        })
    }
}

const updateComponentVerticalMargins = (componentStageData: ComponentStageData, topPadding: number) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = _.cloneDeep(conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {})
    itemLayout.margins = {
        ...itemLayout.margins,
        top: {
            value: topPadding,
            type: 'spx'
        },
        bottom: {
            value: 0,
            type: 'spx'
        }
    }

    updateTargetLayout(componentStageData, {
        itemLayout
    })
}

const updatePaddingBottom = (componentStageData: ComponentStageData, padding: number) => {
    const {conversionDal, componentPointer} = componentStageData
    const {containerLayout} = _.cloneDeep(conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {})
    _.set(containerLayout, ['padding', 'bottom'], {
        value: padding,
        type: 'spx'
    })

    updateTargetLayout(componentStageData, {
        containerLayout
    })
}

const setFitToContentHeight = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer} = componentStageData
    const component = conversionDal.get(componentPointer)
    updatePaddingBottom(componentStageData, BOTTOM_PADDING[component.semanticType])
    updateComponentHeight(componentStageData, 0, HeightType.Auto)
}

const calculateAspectRatioHeight = (
    componentStageData: ComponentStageData,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => {
    const {componentPointer, initialSnapshot, globalContext} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)
    const {horizontalScaleAndSize} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const width = (horizontalScaleAndSize as HorizontalScaleAndSizePluginAPI).getWidth(componentPointer)

    return originalMeasurements.height * (width / originalMeasurements.width)
}

const setAspectRatioHeight = (componentStageData: ComponentStageData) => {
    const {componentPointer, initialSnapshot} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)
    const heightToWidthRatio = originalMeasurements.height / originalMeasurements.width
    updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
}

const setImageHeight = (
    componentStageData: ComponentStageData,
    readOnlyExtensionAPI: ReadOnlyExtensionAPI,
    {
        minHeight,
        maxHeight,
        keepAspectRatio
    }: {
        minHeight: number
        maxHeight: number
        keepAspectRatio?: boolean
    }
) => {
    const {initialSnapshot, componentPointer, globalContext} = componentStageData
    const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
    const {height} = initialSnapshot.getValue(componentMeasurementsPointer)
    const {horizontalScaleAndSize} = readOnlyExtensionAPI.getPluginsApi(globalContext)
    const width = (horizontalScaleAndSize as HorizontalScaleAndSizePluginAPI).getWidth(componentPointer)

    if (keepAspectRatio) {
        const aspectRatioHeight = calculateAspectRatioHeight(componentStageData, readOnlyExtensionAPI)
        const heightToWidthRatio = Math.max(Math.min(aspectRatioHeight, maxHeight), minHeight) / width
        return updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
    }

    const heightToWidthRatio = Math.max(Math.min(height, maxHeight), minHeight) / width
    updateComponentHeight(componentStageData, heightToWidthRatio, HeightType.AspectRatio)
}

const setOriginalHeight = (componentStageData: ComponentStageData) => {
    const {initialSnapshot, componentPointer} = componentStageData

    updateComponentHeight(componentStageData, initialSnapshot.getValue(getMeasurementsPointer(componentPointer)).height)
}

const genericBetweenCompTypesPredicate =
    (lowerCompSemanticType: string, upperCompSemanticType: string) => (componentStageData: ComponentStageData) => {
        const {componentPointer, conversionDal} = componentStageData
        const component = conversionDal.get(componentPointer)
        const containerPointer = getStructurePointer({id: component.parent})
        const container = conversionDal.get(containerPointer)
        const currentCompIndex = container.components!.indexOf(component.id)
        const previousSiblingPointer = getStructurePointer({
            id: container.components![currentCompIndex - 1]
        })
        const previousSibling = conversionDal.get(previousSiblingPointer)

        return (
            component.semanticType === lowerCompSemanticType && previousSibling.semanticType === upperCompSemanticType
        )
    }

const getGenericYHeuristic = (): ComponentStageHeuristicDefinition[] => {
    const heuristics = []

    for (const [lowerCompSemanticType, settings] of Object.entries(TOP_BOTTOM_RELATION)) {
        for (const [upperSemanticCompType, paddingValue] of Object.entries(settings)) {
            heuristics.push({
                predicate: genericBetweenCompTypesPredicate(lowerCompSemanticType, upperSemanticCompType),
                apply: (componentStageData: ComponentStageData) =>
                    updateComponentVerticalMargins(componentStageData, paddingValue),
                heuristicCode: `${lowerCompSemanticType} TOP MARGIN FROM ${upperSemanticCompType}`
            })
        }
    }

    return heuristics
}

export const heightHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.SECTION && !component.components?.length
        },
        apply: (componentStageData: ComponentStageData) => updateComponentHeight(componentStageData, 200),
        heuristicCode: 'EMPTY SECTION HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.SECTION
        },
        apply: setFitToContentHeight,
        heuristicCode: 'SECTION FIT TO CONTENT HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal, initialSnapshot}: ComponentStageData) => {
            const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
            const component = conversionDal.get(componentPointer)
            const originalMeasurements = initialSnapshot.getValue(componentMeasurementsPointer)

            return (
                component.semanticType === SemanticTypes.BOX &&
                !component.components?.length &&
                originalMeasurements.height < 101
            )
        },
        apply: (componentStageData: ComponentStageData) => updateComponentHeight(componentStageData, 100),
        heuristicCode: 'EMPTY SMALL BOX HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.BOX && !component.components?.length
        },
        apply: (componentStageData: ComponentStageData) => updateComponentHeight(componentStageData, 200),
        heuristicCode: 'EMPTY LARGE BOX HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.BOX
        },
        apply: (componentStageData: ComponentStageData) => setFitToContentHeight(componentStageData),
        heuristicCode: 'BOX FIT TO CONTENT HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal, initialSnapshot}: ComponentStageData) => {
            const componentMeasurementsPointer = getMeasurementsPointer(componentPointer)
            const {width} = initialSnapshot.getValue(componentMeasurementsPointer)
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.IMAGE && width < 201
        },
        apply: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) => setImageHeight(componentStageData, readOnlyExtensionAPI, {minHeight: 48, maxHeight: 600}),
        heuristicCode: 'NARROW IMAGE HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.IMAGE
        },
        apply: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI: ReadOnlyExtensionAPI
        ) =>
            setImageHeight(componentStageData, readOnlyExtensionAPI, {
                minHeight: 48,
                maxHeight: 300,
                keepAspectRatio: true
            }),
        heuristicCode: 'IMAGE FALLBACK HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return (
                component.semanticType === SemanticTypes.SVG || component.semanticType === SemanticTypes.EMBEDDED_MEDIA
            )
        },
        apply: setAspectRatioHeight,
        heuristicCode: 'ASPECT RATIO HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.TEXT
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentHeight(componentStageData, 0, HeightType.Auto),
        heuristicCode: 'TEXT FALLBACK HEIGHT'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)

            return component.semanticType === SemanticTypes.BUTTON
        },
        apply: setAspectRatioHeight,
        heuristicCode: 'BUTTON ASPECT RATIO HEIGHT'
    },
    {
        predicate: () => true,
        apply: setOriginalHeight,
        heuristicCode: 'ANY COMPONENT FALLBACK HEIGHT'
    }
]

export const verticalMarginsHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return component.semanticType === SemanticTypes.SECTION
        },
        apply: () => {}
    },
    {
        predicate: (componentStageData: ComponentStageData) => isPinned(componentStageData),
        apply: (componentStageData: ComponentStageData) => {
            //TODO: change this once we have a way heuristic to pinned containers
            if (isPinnedToContainer(componentStageData)) {
                updatePinnedToContainerVerticalMargins(componentStageData)
                return
            }
            updatePinnedVerticalMargins(componentStageData)
        },
        heuristicCode: 'Pinned Element'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            const containerPointer = getStructurePointer({id: component.parent})
            const container = conversionDal.get(containerPointer)
            const currentCompIndex = container.components!.indexOf(component.id)

            return currentCompIndex === 0 && container.semanticType === SemanticTypes.SECTION
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, TOP_PADDING[SemanticTypes.SECTION]),
        heuristicCode: 'FIRST CHILD IN SECTION TOP MARGIN'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            const containerPointer = getStructurePointer({id: component.parent})
            const container = conversionDal.get(containerPointer)
            const currentCompIndex = container.components!.indexOf(component.id)

            return currentCompIndex === 0 && container.semanticType === SemanticTypes.BOX
        },
        apply: () => (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, TOP_PADDING[SemanticTypes.BOX]),
        heuristicCode: 'FIRST CHILD IN BOX TOP MARGIN'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            const virtualParent = conversionDal.get(getVirtualStructurePointer({id: component.virtualParentId}))

            return (
                virtualParent &&
                virtualParent.componentType === VIRTUAL_TAG &&
                virtualParent.components!.indexOf(component.id) === 0
            )
        },
        apply: (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, LIST_ITEM_PADDING_TOP),
        heuristicCode: 'FIRST CHILD IN VIRTUAL LIST ITEM TOP MARGIN'
    },
    ...getGenericYHeuristic(),
    {
        predicate: () => true,
        apply: (componentStageData: ComponentStageData) =>
            updateComponentVerticalMargins(componentStageData, DEFAULT_PADDING_BOTTOM),
        heuristicCode: 'DEFAULT TOP MARGIN'
    }
]
